import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { DotsThreeVertical, ShieldCheck, Prohibit, ArrowCounterClockwise, Trash } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";

const fmtDate = (s) => (s ? format(new Date(s), "MMM d, yyyy") : "—");

export default function AdminUsers() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ role: "all", plan: "all", start: "", end: "" });

  const params = new URLSearchParams();
  if (filters.role !== "all") params.set("role", filters.role);
  if (filters.plan !== "all") params.set("plan", filters.plan);
  if (filters.start) params.set("start", filters.start);
  if (filters.end) params.set("end", filters.end);

  const { data: users = [] } = useQuery({
    queryKey: ["admin-users", filters],
    queryFn: async () => (await api.get(`/admin/users?${params.toString()}`)).data,
  });

  const act = useMutation({
    mutationFn: async ({ url, method = "post", body }) => (await api[method](url, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Action completed.");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const del = (u) => { if (window.confirm(`Delete ${u.email} and all their data? This cannot be undone.`)) act.mutate({ url: `/admin/users/${u.id}`, method: "delete" }); };

  return (
    <div data-testid="admin-users-page">
      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={filters.role} onValueChange={(v) => setFilters({ ...filters, role: v })}>
          <SelectTrigger className="w-40 bg-secondary/50" data-testid="filter-role"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.plan} onValueChange={(v) => setFilters({ ...filters, plan: v })}>
          <SelectTrigger className="w-44 bg-secondary/50" data-testid="filter-plan"><SelectValue placeholder="Subscription" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })}
          className="w-40 bg-secondary/50" data-testid="filter-start" />
        <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })}
          className="w-40 bg-secondary/50" data-testid="filter-end" />
      </div>

      <Card className="bg-card border-border overflow-x-auto">
        <div className="min-w-[900px]">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border overline text-muted-foreground">
            <span className="col-span-2">Name</span><span className="col-span-2">Email</span>
            <span className="col-span-1">Signup</span><span className="col-span-1">Role</span>
            <span className="col-span-1">Plan</span><span className="col-span-2">Last Login</span>
            <span className="col-span-1">Duties</span><span className="col-span-1">Cheques</span>
            <span className="col-span-1 text-right">Actions</span>
          </div>
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`}
              className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border last:border-0 items-center text-sm">
              <span className="col-span-2 font-medium truncate flex items-center gap-1.5">
                {u.suspended && <Prohibit className="h-3.5 w-3.5 text-destructive shrink-0" weight="bold" />}{u.name}
              </span>
              <span className="col-span-2 text-muted-foreground truncate">{u.email}</span>
              <span className="col-span-1 text-muted-foreground text-xs">{fmtDate(u.created_at)}</span>
              <span className="col-span-1">
                <span className={`text-xs font-semibold ${u.role === "admin" ? "text-destructive" : "text-muted-foreground"}`}>{u.role}</span>
              </span>
              <span className="col-span-1">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${u.plan === "pro" ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"}`}>{u.plan}</span>
              </span>
              <span className="col-span-2 text-muted-foreground text-xs">{u.last_login ? fmtDate(u.last_login) : "Never"}{u.suspended && <span className="text-destructive"> · Suspended</span>}</span>
              <span className="col-span-1 text-muted-foreground">{u.paid_duty_count}</span>
              <span className="col-span-1 text-muted-foreground">{u.paycheque_count}</span>
              <div className="col-span-1 flex justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid={`user-actions-${u.id}`} className="p-1 rounded hover:bg-secondary text-muted-foreground">
                      <DotsThreeVertical className="h-5 w-5" weight="bold" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-popover border-border">
                    {u.suspended ? (
                      <DropdownMenuItem data-testid={`unsuspend-${u.id}`} onClick={() => act.mutate({ url: `/admin/users/${u.id}/unsuspend` })}>
                        <ArrowCounterClockwise className="h-4 w-4 mr-2" /> Unsuspend
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem data-testid={`suspend-${u.id}`} onClick={() => act.mutate({ url: `/admin/users/${u.id}/suspend` })}>
                        <Prohibit className="h-4 w-4 mr-2" /> Suspend
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem data-testid={`role-${u.id}`}
                      onClick={() => act.mutate({ url: `/admin/users/${u.id}/role`, body: { role: u.role === "admin" ? "user" : "admin" } })}>
                      <ShieldCheck className="h-4 w-4 mr-2" /> Make {u.role === "admin" ? "User" : "Admin"}
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid={`delete-${u.id}`} onClick={() => del(u)} className="text-destructive">
                      <Trash className="h-4 w-4 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
          {users.length === 0 && <div className="p-8 text-center text-muted-foreground" data-testid="users-empty">No users match these filters.</div>}
        </div>
      </Card>
    </div>
  );
}
